import { useMemo } from 'react';
import type { GridSelection } from '@glideapps/glide-data-grid';
import { STAGE_LABELS, formatRoundLabel, formatRoundMonth } from '@nemo/core/model';
import { actionsCell, chipsCell, type Chip, type GridAction } from '../grid/cells/index.js';
import { orderedColumns, type GridColumnDef } from '../grid/columns.js';
import { DataGrid } from '../grid/DataGrid.js';
import { text } from '../grid/fundColumns.js';
import type { GridPreferences } from '../grid/useGridPreferences.js';
import {
  candidateTypeLabel,
  hqCountriesText,
  knowAnyoneText,
  knowAnyoneTip,
  lastContactText,
  lastContactTip,
} from '../dealroom/format.js';
import { gridTheme } from '../palette.js';
import type { Candidate } from '../dealroom/types.js';
import { formatDay } from '../ui/format.js';
import { GRID_ICON_TEXT_PADDING } from '../ui/SiteIcon.js';
import { sharedRoundsText } from './CoInvestLine.js';
import { factsText } from './FactLine.js';

/** How many fund and company names a cell spells out before "+N"; the hover has them all. */
const NAMES_SHOWN = 3;
/** How many sectors and regions the facts cell names before the rest go to the hover. */
const FACTS_PER_KIND = 2;

/** Whether the rows are tall enough for a long cell to take a second line. */
interface CandidateContext {
  readonly twoLine: boolean;
  /** The candidates the Affinity check under way is still to answer. */
  readonly checking: ReadonlySet<string>;
}

/** A candidate's last contact in the shape the shared formatter reads. */
function candidateContact(candidate: Candidate) {
  return {
    at: candidate.affinity.lastContactAt,
    nextMeetingAt: candidate.affinity.nextMeetingAt,
    recency: candidate.affinity.recency,
    lastEmailAt: candidate.affinity.lastEmailAt,
    lastMeetingAt: candidate.affinity.lastMeetingAt,
    people: candidate.affinity.people,
  };
}

/** "Know anyone?" for a row: the check's pending state while it is being asked. */
function knowAnyoneShown(candidate: Candidate, context: CandidateContext) {
  return context.checking.has(candidate.id)
    ? { text: 'Checking…', faint: '', dim: true }
    : knowAnyoneText(candidate.affinity);
}

const ACTIONS: readonly GridAction[] = [
  { id: 'accept', label: 'Track fund', tone: 'primary' },
  { id: 'ignore', label: 'Dismiss' },
];

/**
 * The funds on our list a candidate sat on a round beside: the ones somebody
 * here knows filled, the rest hollow, so the warm ways in read first.
 */
function coInvestedChips(candidate: Candidate): readonly Chip[] {
  return candidate.coInvestedFunds.slice(0, NAMES_SHOWN).map((fund) => ({
    label: fund.name,
    tone: 'neutral',
    style: fund.inCrm ? undefined : 'hollow',
  }));
}

/** "Led Series A ×3 · Health · DACH": what its own rounds of the last 36 months say. */
function factChips(candidate: Candidate): readonly Chip[] {
  const { facts } = candidate;
  return [
    ...facts.leadsAt.map((fact): Chip => ({
      label: `Led ${STAGE_LABELS[fact.value]}${fact.rounds > 1 ? ` ×${fact.rounds}` : ''}`,
      tone: 'neutral',
      mark: 'lead',
    })),
    ...facts.sectors
      .slice(0, FACTS_PER_KIND)
      .map((fact): Chip => ({ label: fact.value, tone: 'neutral' })),
    ...facts.regions
      .slice(0, FACTS_PER_KIND)
      .map((fact): Chip => ({ label: fact.value, tone: 'neutral' })),
  ];
}

function portfolioText(candidate: Candidate): string {
  const companies = candidate.portfolioCompanies;
  const shown = companies.slice(0, NAMES_SHOWN).map((company) => company.name);
  const more = companies.length - shown.length;
  return `${shown.join(', ')}${more > 0 ? ` +${more}` : ''}`;
}

/** Every Project A company it backed, with the round and the month. */
function portfolioTip(candidate: Candidate): string {
  return candidate.portfolioCompanies
    .map((company) =>
      [
        company.name,
        formatRoundLabel(company.roundLabel),
        company.month === null ? '' : formatRoundMonth(company.month),
      ]
        .filter((part) => part !== '')
        .join(' · '),
    )
    .join('\n');
}

const COLUMNS: readonly GridColumnDef<Candidate, CandidateContext>[] = [
  {
    id: 'name',
    title: 'Name',
    width: 220,
    cell: (candidate) => ({
      ...text(candidate.name, false),
      ...(candidate.website === null ? {} : { cursor: 'pointer' }),
      themeOverride: { cellHorizontalPadding: GRID_ICON_TEXT_PADDING, baseFontStyle: '600 13px' },
    }),
    text: (candidate) => candidate.name,
    links: (candidate) =>
      candidate.website === null ? [] : [{ label: 'Website', href: candidate.website }],
  },
  {
    id: 'knowAnyone',
    title: 'Know anyone?',
    width: 130,
    note: 'Whether somebody at Project A has emailed or met somebody there, as Affinity says. Select rows and use Check Affinity to ask.',
    cell: (candidate, context) => {
      const said = knowAnyoneShown(candidate, context);
      return {
        ...text(said.text, false),
        ...(said.dim ? { themeOverride: { textDark: gridTheme().textLight ?? '' } } : {}),
      };
    },
    faintSuffix: (candidate, context) => knowAnyoneShown(candidate, context).faint,
    text: (candidate) => {
      const said = knowAnyoneText(candidate.affinity);
      return candidate.affinity.status === null ? '' : `${said.text} ${said.faint}`.trim();
    },
  },
  {
    id: 'lastContact',
    title: 'Last contact',
    width: 110,
    note: 'The latest email or meeting between Project A and anybody there, per Affinity. Faint: more than two years ago. Order by it with Sort: Last contact.',
    cell: (candidate) => {
      const said = lastContactText(candidateContact(candidate));
      return {
        ...text(said.text, false),
        ...(said.dim ? { themeOverride: { textDark: gridTheme().textLight ?? '' } } : {}),
      };
    },
    text: (candidate) => lastContactText(candidateContact(candidate)).text,
  },
  {
    id: 'portfolio',
    title: 'Project A portfolio',
    width: 200,
    note: 'The Project A portfolio companies it joined a round of. A click opens one.',
    cell: (candidate) => text(portfolioText(candidate), false),
    text: (candidate) => candidate.portfolioCompanies.map((company) => company.name).join(', '),
    links: (candidate) =>
      candidate.portfolioCompanies.map((company) => ({
        label: company.name,
        href: `/portfolio?company=${company.id}`,
      })),
  },
  {
    id: 'coInvested',
    title: 'Co-invested with',
    width: 260,
    note: 'Funds on our list it sat on a round beside. Filled: somebody here knows them. A click opens one.',
    cell: (candidate) => {
      const more =
        candidate.coInvestedFundCount - Math.min(NAMES_SHOWN, candidate.coInvestedFunds.length);
      return chipsCell(coInvestedChips(candidate), more > 0 ? { note: `+${more}` } : {});
    },
    text: (candidate) => candidate.coInvestedFunds.map((fund) => fund.name).join(', '),
    links: (candidate) =>
      candidate.coInvestedFunds.map((fund) => ({
        label: fund.name,
        href: `/?fund=${fund.fundId}`,
      })),
  },
  {
    id: 'facts',
    title: 'Its rounds',
    width: 260,
    note: 'What its own rounds of the last 36 months say: the stages it led, its strongest sectors and regions.',
    cell: (candidate) => chipsCell(factChips(candidate)),
    text: (candidate) =>
      factChips(candidate)
        .map((chip) => chip.label)
        .join(', '),
  },
  {
    id: 'type',
    title: 'Type',
    width: 130,
    cell: (candidate) => text(candidateTypeLabel(candidate.investorType), false),
    text: (candidate) => candidateTypeLabel(candidate.investorType),
  },
  {
    id: 'hq',
    title: 'HQ',
    width: 110,
    cell: (candidate) => text(hqCountriesText(candidate.hqCountries), false),
    text: (candidate) => candidate.hqCountries.join(', '),
  },
  {
    id: 'roundsSeen',
    title: 'Rounds seen',
    width: 100,
    cell: (candidate) => ({ ...text(String(candidate.roundsSeen), false), contentAlign: 'right' }),
    text: (candidate) => String(candidate.roundsSeen),
  },
  {
    id: 'examples',
    title: 'For instance',
    width: 240,
    cell: (candidate, context) =>
      text(candidate.exampleCompanies.join(', '), false, context.twoLine),
    text: (candidate) => candidate.exampleCompanies.join(', '),
  },
  {
    id: 'lastSeen',
    title: 'Last seen',
    width: 110,
    cell: (candidate) => text(formatDay(candidate.lastSeen) ?? '', false),
    text: (candidate) => formatDay(candidate.lastSeen) ?? '',
  },
  {
    id: 'actions',
    title: '',
    width: 170,
    cell: () => actionsCell(ACTIONS),
    text: () => '',
  },
];

export interface CandidateGridProps {
  rows: readonly Candidate[];
  onDecide: (candidate: Candidate, decision: 'accept' | 'ignore') => void;
  hasMore: boolean;
  loadingMore: boolean;
  onReachEnd: () => void;
  empty: React.ReactNode;
  preferences: GridPreferences;
  /** Which rows are ticked, for a check over them. */
  selection: { value: GridSelection; onChange: (selection: GridSelection) => void };
  /** The rows the Affinity check under way is still to answer. */
  checking: ReadonlySet<string>;
}

/**
 * The Discovered backlog read down a list, in the order the toolbar chose:
 * the server sorts it and sends it a page at a time, so the headers do not.
 * Each row is tracked or dismissed from its own buttons or its right-click
 * menu; a name that holds several links offers them as a menu.
 */
export function CandidateGrid(props: CandidateGridProps): React.JSX.Element {
  const { preferences, onDecide } = props;
  const columns = useMemo(() => orderedColumns(COLUMNS, preferences), [preferences]);
  const twoLine = preferences.density === 'comfortable';
  const { checking } = props;
  const context = useMemo(() => ({ twoLine, checking }), [twoLine, checking]);

  return (
    <DataGrid
      rows={props.rows}
      columns={columns}
      context={context}
      rowId={(candidate) => candidate.id}
      preferences={preferences}
      paging={{ hasMore: props.hasMore, loading: props.loadingMore, onReachEnd: props.onReachEnd }}
      siteIcon={{ columnId: 'name', of: (candidate) => candidate }}
      onAction={(candidate, action) => {
        if (action === 'accept' || action === 'ignore') onDecide(candidate, action);
      }}
      rowMenu={{
        label: 'Discovered fund',
        items: (candidate) => [
          { label: 'Track fund', onSelect: () => onDecide(candidate, 'accept') },
          { label: 'Dismiss', onSelect: () => onDecide(candidate, 'ignore') },
          ...(candidate.website === null
            ? []
            : [
                {
                  label: 'Open website',
                  onSelect: () => window.open(candidate.website ?? '', '_blank', 'noopener'),
                },
              ]),
        ],
      }}
      tip={tipFor}
      freezeColumns={1}
      selection={props.selection}
      multiSelect
      empty={props.empty}
    />
  );
}

function tipFor(
  candidate: Candidate,
  column: GridColumnDef<Candidate, CandidateContext>,
): string | null {
  switch (column.id) {
    case 'knowAnyone':
      return knowAnyoneTip(candidate.affinity);
    case 'lastContact':
      return lastContactTip(candidateContact(candidate));
    case 'portfolio':
      return candidate.portfolioCompanies.length === 0 ? null : portfolioTip(candidate);
    case 'coInvested':
      return candidate.coInvestedFundCount === 0 && candidate.portfolioCompanies.length === 0
        ? null
        : sharedRoundsText(candidate);
    case 'facts':
      return factChips(candidate).length === 0 ? null : factsText(candidate);
    case 'hq':
      return candidate.hqCountries.length === 0 ? null : candidate.hqCountries.join(', ');
    case 'examples':
      return candidate.exampleCompanies.length === 0 ? null : candidate.exampleCompanies.join('\n');
    default:
      return null;
  }
}
